/**
 * GRÁFICOS DEL CATÁLOGO — material provisional, autorado en código.
 * =============================================================================
 *
 * QUÉ SON Y QUÉ NO SON
 *
 * Son **ilustración de categoría**: dicen qué ES una celosía, un diferencial o una
 * tierra física. **No son fotografía de obra de Soham Home** y nunca deben rotularse
 * como tal — PRODUCT.md declara explícitamente que todavía no hay fotos de trabajo
 * real, y esa ausencia no se fabrica. Cada uno lleva `provisional:true` para que la
 * interfaz pueda declararlo cuando haga falta.
 *
 * POR QUÉ EN CÓDIGO Y NO GENERADAS
 *
 * En esta sesión no hay herramienta de generación de imágenes (se buscó por «image
 * generation», «create image», «render» e «illustration»: solo aparecen herramientas
 * para subir o insertar, ninguna para crear). Dibujarlas es la vía honesta, y para
 * este producto es además la mejor: la celosía y el lambrín salen de un patrón
 * paramétrico, que es exactamente como los corta la CNC del taller. Un patrón
 * generado por código es más verdadero aquí que una fotografía inventada.
 *
 * LA REGLA DE COMPOSICIÓN, que es la que arregla el problema anterior
 *
 * El catálogo viejo ponía una figura blanca pequeña, centrada, sobre un cuadro con
 * degradado. Eso se lee como icono roto, no como material. Aquí **el dibujo llena el
 * cuadro y se corta por los cuatro lados**: es un recorte del material, a escala, no
 * un símbolo flotando. Ninguna figura se repite entre productos — el catálogo viejo
 * usaba el mismo dibujo para «Salida de contacto» y «Contacto GFCI», y para los tres
 * productos de automatización.
 *
 * EL FONDO COMPARTIDO
 *
 * Los 23 comparten una tinta verde-negra (`TINTA`) para que la pared de mosaicos se
 * lea como una sola familia y como Soham. El color propio de cada material —cobre,
 * nogal, galvanizado, ámbar de lámpara— es lo que los distingue. El verde de marca
 * (#059669) NO se usa aquí dentro: queda reservado al acento de la interfaz, que es
 * lo que lo mantiene valioso.
 *
 * SUSTITUCIÓN
 *
 * Cuando existan fotos de obra real, se sustituyen por archivo y se borra la regla
 * de `estilos.css` que hoy trata a estos gráficos como material provisional.
 * ============================================================================= */

/* ---- Paleta de materiales. Cada tono existe porque el material lo tiene. ---- */
const TINTA     = '#141E1A'   // verde-negro: el fondo común de toda la familia
const TINTA_2   = '#1C2A24'   // su hermano claro, para dar profundidad
const HUESO     = '#F2EFE9'
const HUESO_2   = '#D8D2C6'
const COBRE     = '#B4703F'
const COBRE_CLR = '#D9945F'
const NOGAL     = '#8A5A34'
const NOGAL_CLR = '#B98553'
const AMBAR     = '#E8B75E'
const GALVA     = '#9AA3A1'
const GALVA_CLR = '#C3CAC7'
const PIZARRA   = '#4A5A57'
const TIERRA_C  = '#6B5641'
const ALERTA    = '#D9633F'

/**
 * Envuelve una figura en el marco común. La figura YA viene a sangre.
 *
 * Encima de cada figura va una CAPA DE LUZ: una caída de claro a oscuro y un viñeteo
 * en las esquinas. No es adorno — resuelve un problema medido: en la pared conviven
 * estos dibujos con las seis fotografías de marca, y sin luz los dibujos se veían
 * planos y de otro medio al lado de una foto. La luz los pone en la misma escena.
 */
const marco = (fondo, figura) =>
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">` +
    `<defs>` +
      `<linearGradient id="_lz" x1="0" y1="0" x2="0" y2="1">` +
        `<stop offset="0" stop-color="#fff" stop-opacity=".14"/>` +
        `<stop offset=".55" stop-color="#fff" stop-opacity="0"/>` +
        `<stop offset="1" stop-color="#000" stop-opacity=".30"/></linearGradient>` +
      `<radialGradient id="_vg" cx=".5" cy=".42" r=".78">` +
        `<stop offset=".55" stop-color="#000" stop-opacity="0"/>` +
        `<stop offset="1" stop-color="#000" stop-opacity=".42"/></radialGradient>` +
    `</defs>` +
    `<rect width="400" height="300" fill="${fondo}"/>${figura}` +
    `<rect width="400" height="300" fill="url(#_lz)"/>` +
    `<rect width="400" height="300" fill="url(#_vg)"/></svg>`
  )

/* ============================================================================
   PATRONES PARAMÉTRICOS DEL TALLER
   Estos dos no son ilustración: son el patrón real, y por eso se generan con la
   misma lógica con la que se corta. Cambiar `paso` cambia la pieza.
   ========================================================================== */

/** Celosía SAT: retícula girada 45° con estrella en cada cruce. Cortable. */
function celosiaSAT (paso = 46, trazo = 7, color = NOGAL, luz = AMBAR) {
  let d = ''
  for (let x = -paso; x <= 400 + paso; x += paso) d += `M${x} -20V320`
  for (let y = -paso; y <= 300 + paso; y += paso) d += `M-20 ${y}H420`
  let diag = ''
  for (let i = -14; i <= 14; i++) {
    diag += `M${i * paso - 40} -20L${i * paso + 340} 320`
    diag += `M${i * paso - 40} 320L${i * paso + 340} -20`
  }
  return (
    `<rect width="400" height="300" fill="${luz}" opacity=".16"/>` +
    `<path d="${d}" stroke="${color}" stroke-width="${trazo}" fill="none"/>` +
    `<path d="${diag}" stroke="${color}" stroke-width="${trazo * .62}" fill="none" opacity=".92"/>`
  )
}

/** Lambrín NOG: duelas verticales con veta y sombra de junta. Distinto de la celosía. */
function lambrinNOG (ancho = 34) {
  let s = ''
  for (let x = 0; x < 400; x += ancho) {
    const t = (x / ancho) % 3
    const tono = t === 0 ? NOGAL : t === 1 ? NOGAL_CLR : '#7A4E2C'
    s += `<rect x="${x}" y="-10" width="${ancho - 3}" height="320" fill="${tono}"/>`
    s += `<rect x="${x + ancho - 3}" y="-10" width="3" height="320" fill="${TINTA}" opacity=".55"/>`
    // veta
    s += `<path d="M${x + 7} -10q6 80 -1 160t3 170" stroke="#000" stroke-width="1.6"
            fill="none" opacity=".16"/>`
    s += `<path d="M${x + ancho - 12} -10q-5 100 2 190t-2 140" stroke="#fff" stroke-width="1.4"
            fill="none" opacity=".10"/>`
  }
  return s
}

/* ============================================================================
   FIGURAS — una por concepto, todas a sangre, ninguna repetida.
   ========================================================================== */

const F = {

  /* ---------- Categorías ---------- */

  // Iluminación: dos conos de luz cayendo desde el techo sobre el plano.
  catIluminacion: `
    <path d="M96 -10L4 300h184z" fill="${AMBAR}" opacity=".30"/>
    <path d="M292 -10L206 300h172z" fill="${AMBAR}" opacity=".18"/>
    <circle cx="96" cy="6" r="17" fill="${AMBAR}"/>
    <circle cx="292" cy="6" r="17" fill="${AMBAR}" opacity=".75"/>
    <rect y="268" width="400" height="32" fill="${HUESO}" opacity=".13"/>`,

  // Contactos: pared de placas, cortada por los bordes.
  catContactos: `
    ${[0, 1, 2].map(r => [0, 1, 2, 3].map(c => {
      const x = c * 104 - 24, y = r * 108 - 30
      return `<rect x="${x}" y="${y}" width="82" height="92" rx="9" fill="${HUESO}" opacity=".93"/>
        <rect x="${x + 22}" y="${y + 22}" width="9" height="26" rx="4" fill="${TINTA}"/>
        <rect x="${x + 51}" y="${y + 22}" width="9" height="26" rx="4" fill="${TINTA}"/>
        <circle cx="${x + 41}" cy="${y + 66}" r="7" fill="${TINTA}"/>`
    }).join('')).join('')}`,

  // Tablero: pastillas montadas en riel, vistas de frente y cortadas por los bordes.
  // La versión anterior eran barras grises con bloques rojos y se leía como una barra
  // de progreso rota; ahora se reconoce el aparato: cuerpo, palanca y barra de cobre.
  catTablero: `
    <rect y="-10" width="400" height="320" fill="${PIZARRA}" opacity=".42"/>
    <rect x="-10" y="128" width="420" height="44" fill="${COBRE}" opacity=".9"/>
    ${[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
      const x = i * 52 - 26
      return `<rect x="${x}" y="18" width="44" height="108" rx="4" fill="${GALVA_CLR}"/>
        <rect x="${x}" y="174" width="44" height="108" rx="4" fill="${GALVA_CLR}" opacity=".82"/>
        <rect x="${x + 12}" y="${i % 3 ? 34 : 52}" width="20" height="34" rx="3"
              fill="${i % 3 ? TINTA : ALERTA}"/>
        <rect x="${x + 12}" y="${i % 2 ? 226 : 208}" width="20" height="34" rx="3" fill="${TINTA}"/>
        <rect x="${x + 6}" y="100" width="32" height="8" rx="2" fill="${TINTA}" opacity=".35"/>`
    }).join('')}`,

  // Seguridad: varilla de tierra clavada en estratos de suelo.
  catSeguridad: `
    <rect y="96" width="400" height="204" fill="${TIERRA_C}" opacity=".55"/>
    <rect y="164" width="400" height="136" fill="${TIERRA_C}" opacity=".75"/>
    <rect y="232" width="400" height="68" fill="${TIERRA_C}"/>
    <rect x="188" y="-10" width="22" height="310" rx="4" fill="${COBRE}"/>
    <rect x="188" y="-10" width="8" height="310" fill="${COBRE_CLR}" opacity=".7"/>
    <path d="M60 96h330M20 164h360M90 232h300" stroke="${HUESO}" stroke-width="2" opacity=".18"/>`,

  // Automatización: malla de nodos y enlaces, a sangre.
  catAutomatizacion: `
    <path d="M-20 84L120 30 250 118 400 62M-20 214L120 30M120 30L86 300M250 118L200 300
             M250 118L400 214M-20 214L250 118" stroke="${HUESO}" stroke-width="2.4"
          fill="none" opacity=".38"/>
    ${[[120, 30], [250, 118], [86, 268], [340, 196], [30, 150], [200, 276]]
      .map(([x, y], i) => `<circle cx="${x}" cy="${y}" r="${i % 2 ? 13 : 20}" fill="${HUESO}"
        opacity="${i % 2 ? .55 : .95}"/>`).join('')}`,

  // Medición: la senoidal con su área bajo curva llena. Vacía se veía como un cable
  // suelto sobre negro; llena se lee como energía, que es lo que se está midiendo.
  catMedicion: `
    <path d="M-20 300V150q40 -116 80 0t80 0 80 0 80 0 80 0V300z" fill="${AMBAR}" opacity=".26"/>
    <path d="M-20 150q40 -116 80 0t80 0 80 0 80 0 80 0" stroke="${AMBAR}"
          stroke-width="8" fill="none"/>
    <path d="M-20 150q40 116 80 0t80 0 80 0 80 0 80 0" stroke="${COBRE_CLR}"
          stroke-width="6" fill="none" opacity=".6"/>
    <path d="M-20 92h440M-20 208h440" stroke="${HUESO}" stroke-width="2"
          stroke-dasharray="10 10" opacity=".35"/>
    <path d="M-20 150h440" stroke="${HUESO}" stroke-width="1.5" opacity=".22"/>`,

  // Arquitectónico: la celosía del taller, a sangre.
  catArquitectonico: celosiaSAT(52, 8),

  // Diagnóstico: lo que la cámara termográfica ve DE VERDAD en un tablero — la
  // silueta de las pastillas con una sola caliente. La versión anterior era un
  // degradado radial naranja y morado sobre negro: no decía nada del oficio y era
  // exactamente el adorno de degradado que el piso de calidad marca como defecto.
  catDiagnostico: `
    <defs><radialGradient id="hot" cx=".5" cy=".5" r=".5">
      <stop offset="0" stop-color="#FFF3B0"/><stop offset=".3" stop-color="#F5A623"/>
      <stop offset=".62" stop-color="${ALERTA}" stop-opacity=".85"/>
      <stop offset="1" stop-color="${ALERTA}" stop-opacity="0"/>
    </radialGradient></defs>
    <rect width="400" height="300" fill="#241A3A"/>
    ${[0, 1, 2, 3, 4, 5, 6].map(i => {
      const x = i * 58 - 20
      // Gradiente de temperatura: frío a la izquierda, la caliente es la cuarta.
      const t = i === 3 ? '#E86A2B' : i === 2 || i === 4 ? '#7A5A9E' : '#3E3468'
      return `<rect x="${x}" y="60" width="48" height="180" rx="5" fill="${t}"/>
        <rect x="${x + 10}" y="96" width="28" height="52" rx="4" fill="${t}" opacity=".55"/>`
    }).join('')}
    <circle cx="216" cy="150" r="122" fill="url(#hot)"/>`,

  /* ---------- Productos ---------- */

  // Salida de contacto: una placa dúplex, enorme, cortada abajo.
  salidaContacto: `
    <rect x="76" y="-24" width="248" height="330" rx="20" fill="${HUESO}"/>
    <rect x="132" y="34" width="26" height="74" rx="11" fill="${TINTA}"/>
    <rect x="242" y="34" width="26" height="74" rx="11" fill="${TINTA}"/>
    <circle cx="200" cy="148" r="19" fill="${TINTA}"/>
    <rect x="132" y="196" width="26" height="74" rx="11" fill="${TINTA}" opacity=".75"/>
    <rect x="242" y="196" width="26" height="74" rx="11" fill="${TINTA}" opacity=".75"/>`,

  // GFCI: la misma placa PERO con los botones de prueba y reposición. Se distingue.
  contactoGFCI: `
    <rect x="76" y="-24" width="248" height="330" rx="20" fill="${HUESO}"/>
    <rect x="120" y="18" width="72" height="40" rx="7" fill="${ALERTA}"/>
    <rect x="208" y="18" width="72" height="40" rx="7" fill="${TINTA}" opacity=".22"/>
    <rect x="132" y="104" width="26" height="70" rx="11" fill="${TINTA}"/>
    <rect x="242" y="104" width="26" height="70" rx="11" fill="${TINTA}"/>
    <circle cx="200" cy="212" r="19" fill="${TINTA}"/>
    <path d="M120 78h160" stroke="${TINTA}" stroke-width="2" opacity=".28"/>`,

  // Salida de iluminación: roseta de techo y su cono, cortado abajo.
  salidaIluminacion: `
    <rect y="-10" width="400" height="52" fill="${HUESO}" opacity=".14"/>
    <path d="M200 42L36 300h328z" fill="${AMBAR}" opacity=".34"/>
    <circle cx="200" cy="42" r="30" fill="${AMBAR}"/>
    <circle cx="200" cy="42" r="52" fill="${AMBAR}" opacity=".28"/>
    <rect x="186" y="-10" width="28" height="46" fill="${GALVA}"/>`,

  // Apagador de escalera: dos apagadores y los viajeros cruzados. Diagrama real.
  apagadorEscalera: `
    <rect x="-10" y="86" width="128" height="128" rx="14" fill="${HUESO}"/>
    <rect x="282" y="86" width="128" height="128" rx="14" fill="${HUESO}"/>
    <rect x="34" y="120" width="42" height="60" rx="7" fill="${TINTA}"/>
    <rect x="326" y="120" width="42" height="60" rx="7" fill="${TINTA}" opacity=".45"/>
    <path d="M118 122L282 178M118 178L282 122" stroke="${COBRE}" stroke-width="7" fill="none"/>
    <circle cx="200" cy="150" r="11" fill="${AMBAR}"/>`,

  // Circuito dedicado 20A: un solo conductor grueso y su pastilla.
  circuitoDedicado: `
    <rect x="-10" y="112" width="420" height="76" rx="8" fill="${TINTA_2}"/>
    <rect x="-10" y="128" width="420" height="44" rx="6" fill="${COBRE}"/>
    <rect x="-10" y="128" width="420" height="15" fill="${COBRE_CLR}" opacity=".65"/>
    <rect x="252" y="70" width="96" height="160" rx="12" fill="${GALVA_CLR}"/>
    <rect x="278" y="120" width="44" height="60" rx="7" fill="${TINTA}"/>
    <text x="300" y="106" font-family="sans-serif" font-size="30" font-weight="600"
          fill="${TINTA}" text-anchor="middle">20</text>`,

  // Centro de carga: el tablero completo, con sus dos columnas.
  centroDeCarga: `
    <rect x="24" y="-16" width="352" height="332" rx="14" fill="${GALVA}"/>
    <rect x="46" y="6" width="308" height="288" rx="8" fill="${TINTA_2}"/>
    ${[0, 1, 2, 3, 4, 5].map(i => `
      <rect x="60" y="${20 + i * 46}" width="128" height="34" rx="5" fill="${GALVA_CLR}"/>
      <rect x="212" y="${20 + i * 46}" width="128" height="34" rx="5" fill="${GALVA_CLR}"/>
      <rect x="${i % 2 ? 96 : 140}" y="${28 + i * 46}" width="26" height="18" rx="3" fill="${TINTA}"/>
      <rect x="${i % 2 ? 292 : 248}" y="${28 + i * 46}" width="26" height="18" rx="3" fill="${TINTA}"/>`).join('')}`,

  // Tierra física: la varilla entrando, con el registro y el cable desnudo.
  tierraFisica: `
    <rect y="70" width="400" height="230" fill="${TIERRA_C}" opacity=".6"/>
    <rect y="150" width="400" height="150" fill="${TIERRA_C}" opacity=".85"/>
    <rect x="176" y="24" width="26" height="290" rx="5" fill="${COBRE}"/>
    <rect x="176" y="24" width="9" height="290" fill="${COBRE_CLR}" opacity=".75"/>
    <path d="M189 70q-70 -6 -104 -70" stroke="${COBRE_CLR}" stroke-width="9" fill="none"/>
    <rect x="132" y="42" width="116" height="46" rx="8" fill="${GALVA}" opacity=".9"/>
    <path d="M0 70h400" stroke="${HUESO}" stroke-width="2.5" opacity=".3"/>`,

  // Diferencial: el toroide y las dos corrientes que deben sumar cero.
  proteccionDiferencial: `
    <ellipse cx="200" cy="150" rx="126" ry="94" fill="none" stroke="${GALVA_CLR}" stroke-width="26"/>
    <ellipse cx="200" cy="150" rx="126" ry="94" fill="none" stroke="${HUESO}"
             stroke-width="4" opacity=".35"/>
    <path d="M-10 118h420" stroke="${COBRE}" stroke-width="15"/>
    <path d="M-10 186h420" stroke="${COBRE_CLR}" stroke-width="15"/>
    <path d="M330 40l30 30-30 30" stroke="${ALERTA}" stroke-width="9" fill="none"
          stroke-linecap="round" stroke-linejoin="round"/>`,

  // Punto de luz automatizado: el módulo detrás de la placa, con el punto encendido.
  puntoAutomatizado: `
    <path d="M280 60L182 300h196z" fill="${AMBAR}" opacity=".26"/>
    <circle cx="280" cy="60" r="22" fill="${AMBAR}"/>
    <rect x="-10" y="88" width="176" height="128" rx="14" fill="${HUESO}"/>
    <rect x="20" y="118" width="112" height="26" rx="5" fill="${TINTA}" opacity=".8"/>
    <rect x="20" y="160" width="66" height="26" rx="5" fill="${TINTA}" opacity=".35"/>
    <path d="M166 152h58" stroke="${COBRE}" stroke-width="8"/>`,

  // Punto atenuable: la rampa de atenuación en escalones. Nada que ver con el anterior.
  puntoAtenuable: `
    ${[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
      const x = i * 50, h = 34 + i * 34
      return `<rect x="${x}" y="${300 - h}" width="46" height="${h}" fill="${AMBAR}"
        opacity="${(.16 + i * .11).toFixed(2)}"/>`
    }).join('')}
    <path d="M-10 266L410 28" stroke="${HUESO}" stroke-width="3" opacity=".5"
          stroke-dasharray="10 8"/>`,

  // Tablero de 4 circuitos: cuatro canales de relevador, numerados por presencia.
  tableroCuatro: `
    <rect x="-10" y="-10" width="420" height="320" fill="${TINTA_2}"/>
    ${[0, 1, 2, 3].map(i => {
      const y = i * 74 + 8
      return `<rect x="-10" y="${y}" width="330" height="56" rx="7" fill="${GALVA_CLR}"
          opacity="${(1 - i * .13).toFixed(2)}"/>
        <rect x="40" y="${y + 16}" width="88" height="24" rx="4" fill="${TINTA}"/>
        <circle cx="288" cy="${y + 28}" r="13" fill="${i < 3 ? AMBAR : TINTA}"
          opacity="${i < 3 ? 1 : .35}"/>
        <path d="M320 ${y + 28}h90" stroke="${COBRE}" stroke-width="7"/>`
    }).join('')}`,

  // Medición de consumo: tres fases desfasadas, con la pinza abrazando una.
  medicionConsumo: `
    ${[0, 1, 2].map(i => {
      const off = i * 44, op = [1, .6, .38][i]
      return `<path d="M-20 ${150 + off - 44}q40 -76 80 0t80 0 80 0 80 0 80 0"
        stroke="${[AMBAR, COBRE_CLR, GALVA_CLR][i]}" stroke-width="6" fill="none" opacity="${op}"/>`
    }).join('')}
    <ellipse cx="300" cy="150" rx="58" ry="76" fill="none" stroke="${HUESO}"
             stroke-width="16" opacity=".92"/>
    <path d="M300 74v-56" stroke="${HUESO}" stroke-width="16" opacity=".92"/>`,

  // Celosía SAT: el patrón fino, que es el que se corta a 4,900/m².
  celosiaSat: celosiaSAT(38, 6),

  // Lambrín NOG: duelas. Deliberadamente distinto de la celosía.
  lambrinNog: lambrinNOG(34),

  // Revisión: la carátula del megóhmetro, que es el instrumento del método.
  revisionSeguridad: `
    <circle cx="200" cy="164" r="168" fill="${HUESO}"/>
    <circle cx="200" cy="164" r="138" fill="${TINTA_2}"/>
    <path d="M78 116a138 138 0 0 1 244 0" fill="none" stroke="${ALERTA}" stroke-width="15"/>
    <path d="M112 82a138 138 0 0 1 176 0" fill="none" stroke="${AMBAR}" stroke-width="15"/>
    ${[0, 1, 2, 3, 4, 5, 6].map(i => {
      const a = Math.PI * (1 + i / 6)
      return `<path d="M${200 + Math.cos(a) * 118} ${164 + Math.sin(a) * 118}
        L${200 + Math.cos(a) * 100} ${164 + Math.sin(a) * 100}"
        stroke="${HUESO}" stroke-width="4" opacity=".6"/>`
    }).join('')}
    <path d="M200 164L286 96" stroke="${HUESO}" stroke-width="8" stroke-linecap="round"/>
    <circle cx="200" cy="164" r="16" fill="${HUESO}"/>`
}

/* ============================================================================
   MAPA PÚBLICO
   ========================================================================== */

/** Fondo por gráfico. Casi todos comparten TINTA: es lo que hace familia. */
const FONDO = {
  catArquitectonico: '#2A1C10',
  celosiaSat: '#2A1C10',
  lambrinNog: '#2A1C10',
  catDiagnostico: TINTA,
  revisionSeguridad: TINTA
}

const G = k => marco(FONDO[k] || TINTA, F[k])

/** Gráfico por id de categoría. */
export const GRAFICO_CATEGORIA = {
  ilu: G('catIluminacion'),
  cto: G('catContactos'),
  tab: G('catTablero'),
  seg: G('catSeguridad'),
  aut: G('catAutomatizacion'),
  med: G('catMedicion'),
  arq: G('catArquitectonico'),
  dia: G('catDiagnostico')
}

/** Gráfico por SKU de producto. Uno por SKU: ninguno se repite. */
export const GRAFICO_PRODUCTO = {
  'SAL-CTO':   G('salidaContacto'),
  'SAL-CTO-P': G('contactoGFCI'),
  'SAL-ILU':   G('salidaIluminacion'),
  'SAL-ESC':   G('apagadorEscalera'),
  'CTO-DED':   G('circuitoDedicado'),
  'CENTRO-C':  G('centroDeCarga'),
  'TIERRA':    G('tierraFisica'),
  'DIFEREN':   G('proteccionDiferencial'),
  'SH-LUZ':    G('puntoAutomatizado'),
  'SH-DIM':    G('puntoAtenuable'),
  'SH-TAB4':   G('tableroCuatro'),
  'SH-MED':    G('medicionConsumo'),
  'TZ-CEL':    G('celosiaSat'),
  'TZ-LAM':    G('lambrinNog'),
  'REVISION':  G('revisionSeguridad')
}

/* ============================================================================
   EL TALLER — 8 categorías y 95 piezas
   ---------------------------------------------------------------------------
   Las 95 piezas no llevan un dibujo hecho a mano cada una: eso no aportaría nada
   y sería falso detalle. Llevan lo que realmente son —**una pieza cortada en la
   CNC**— resuelta como patrón paramétrico sembrado con su propia clave. Ninguna
   pieza queda vacía y **ningún par de piezas comparte patrón**, porque la semilla
   sale del SKU. Cambiar la semilla cambia el corte, igual que en el taller.
   ========================================================================== */

/** Semilla estable a partir del SKU. Sin aleatoriedad: el mismo SKU da el mismo corte. */
function semilla (sku) {
  let h = 2166136261
  for (let i = 0; i < sku.length; i++) { h ^= sku.charCodeAt(i); h = Math.imul(h, 16777619) }
  return Math.abs(h)
}

/** Familia de cortes que el taller sabe hacer. Cada uno llena el cuadro. */
const CORTES = [
  // Retícula recta
  (p, t, c) => { let d = ''
    for (let x = -p; x <= 420; x += p) d += `M${x} -20V320`
    for (let y = -p; y <= 320; y += p) d += `M-20 ${y}H420`
    return `<path d="${d}" stroke="${c}" stroke-width="${t}" fill="none"/>` },
  // Rombo
  (p, t, c) => { let d = ''
    for (let i = -16; i <= 16; i++) { d += `M${i * p - 40} -20L${i * p + 340} 320`; d += `M${i * p - 40} 320L${i * p + 340} -20` }
    return `<path d="${d}" stroke="${c}" stroke-width="${t}" fill="none"/>` },
  // Hexágono
  (p, t, c) => { let s = ''
    for (let r = -1; r < 300 / (p * .87) + 1; r++) for (let q = -1; q < 400 / p + 1; q++) {
      const cx = q * p + (r % 2 ? p / 2 : 0), cy = r * p * .87
      const pts = [0, 1, 2, 3, 4, 5].map(k => {
        const a = Math.PI / 180 * (60 * k - 30)
        return `${(cx + Math.cos(a) * p * .46).toFixed(1)},${(cy + Math.sin(a) * p * .46).toFixed(1)}`
      }).join(' ')
      s += `<polygon points="${pts}" fill="none" stroke="${c}" stroke-width="${t}"/>` }
    return s },
  // Espiga
  (p, t, c) => { let d = ''
    for (let y = -p; y <= 320; y += p) for (let x = -p; x <= 420; x += p * 2)
      d += `M${x} ${y + p}L${x + p} ${y}L${x + p * 2} ${y + p}`
    return `<path d="${d}" stroke="${c}" stroke-width="${t}" fill="none"/>` },
  // Duela ondulada
  (p, t, c) => { let d = ''
    for (let y = -p; y <= 320; y += p) d += `M-20 ${y}q50 -${p * .7} 100 0t100 0 100 0 100 0 100 0`
    return `<path d="${d}" stroke="${c}" stroke-width="${t}" fill="none"/>` },
  // Perforación
  (p, t, c) => { let s = ''
    for (let y = 0; y <= 300 + p; y += p) for (let x = 0; x <= 400 + p; x += p)
      s += `<circle cx="${x + (Math.floor(y / p) % 2 ? p / 2 : 0)}" cy="${y}" r="${p * .27}" fill="${c}"/>`
    return s },
  // Radial
  (p, t, c) => { let s = ''
    for (let r = p; r < 460; r += p) s += `<circle cx="60" cy="300" r="${r}" fill="none" stroke="${c}" stroke-width="${t}"/>`
    for (let k = 0; k < 9; k++) { const a = -Math.PI / 2 * (k / 8)
      s += `<path d="M60 300L${60 + Math.cos(a) * 460} ${300 + Math.sin(a) * 460}" stroke="${c}" stroke-width="${t}"/>` }
    return s }
]

/** Maderas del taller. El tono también sale de la semilla, así la pared no se aplana. */
const MADERAS = [
  ['#2A1C10', NOGAL_CLR], ['#241A14', '#C89A6A'], ['#31210F', '#A9norm'],
  ['#2E2014', '#B98553'], ['#1F1A14', '#9A7A52'], ['#33240F', '#D2A473']
].map(([f, c]) => [f, c === '#A9norm' ? '#A98B62' : c])

/**
 * Gráfico determinista para cualquier pieza del taller.
 *
 * Usa DOS semillas independientes y seis parámetros —corte, madera, paso, trazo,
 * giro y desplazamiento—. Con una sola semilla y cuatro parámetros quedaban cuatro
 * pares de piezas con el mismo dibujo entre las 95; verificado con `chk.mjs`, ahora
 * no queda ninguno. La condición es dura: dos piezas distintas no comparten corte.
 */
export function graficoTaller (sku) {
  const a = semilla(sku)
  const b = semilla(sku.split('').reverse().join('') + '·')
  const corte = CORTES[a % CORTES.length]
  const [fondo, color] = MADERAS[(a >> 3) % MADERAS.length]
  const paso = 24 + ((a >> 6) % 7) * 8         // 24 … 72
  const trazo = 3.5 + ((b >> 2) % 5) * 1.4     // 3.5 … 9.1
  const giro = ((b >> 7) % 8) * 11.25          // 0 … 78.75°
  const dx = ((b >> 12) % 5) * 7               // 0 … 28
  return marco(fondo,
    `<rect width="400" height="300" fill="${AMBAR}" opacity=".07"/>` +
    `<g transform="rotate(${giro} 200 150) translate(${dx} ${dx / 2})">` +
    corte(paso, trazo, color) + `</g>`)
}

/** Gráfico por id de categoría del taller. Cada uno es su propio material. */
export const GRAFICO_CATEGORIA_TALLER = {
  // Sombra y exteriores: duelas de pérgola y la sombra que proyectan.
  ext: marco('#2A1C10',
    `${[0, 1, 2, 3, 4, 5, 6].map(i => `<rect x="-10" y="${i * 46 - 8}" width="420" height="22"
       fill="${NOGAL_CLR}"/>`).join('')}
     ${[0, 1, 2, 3, 4, 5, 6].map(i => `<rect x="-10" y="${i * 46 + 16}" width="420" height="24"
       fill="${AMBAR}" opacity=".22"/>`).join('')}`),
  // Fachada: relieve de placas con junta profunda.
  fach: marco('#241A14',
    `${[0, 1, 2, 3].map(r => [0, 1, 2, 3, 4].map(c => `<rect x="${c * 84 - 12}" y="${r * 82 - 12}"
       width="76" height="74" rx="3" fill="${NOGAL}" opacity="${.55 + ((r + c) % 3) * .18}"/>`).join('')).join('')}
     <path d="M0 70h400M0 152h400M0 234h400" stroke="${TINTA}" stroke-width="6" opacity=".55"/>`),
  // Interiores: lambrín en diagonal — distinto del vertical del producto.
  int: marco('#2E2014',
    `${[...Array(18)].map((_, i) => `<path d="M${i * 40 - 320} 320L${i * 40 + 40} -20"
       stroke="${NOGAL_CLR}" stroke-width="18" opacity="${.5 + (i % 3) * .2}"/>`).join('')}`),
  // Muebles: perfil de ensamble a caja y espiga, cortado.
  mue: marco('#1F1A14',
    `<rect x="-10" y="96" width="230" height="120" fill="${NOGAL}"/>
     <rect x="220" y="-10" width="120" height="320" fill="${NOGAL_CLR}"/>
     ${[0, 1, 2, 3].map(i => `<rect x="196" y="${104 + i * 30}" width="52" height="18"
        fill="${i % 2 ? NOGAL_CLR : NOGAL}"/>`).join('')}
     <path d="M220 -10v320" stroke="${TINTA}" stroke-width="4" opacity=".5"/>`),
  // Arte de pared: trama de medios tonos, que es como se sublima una imagen.
  art: marco('#221A16',
    `${[...Array(11)].map((_, r) => [...Array(15)].map((_, c) => {
      const d = Math.hypot(r - 5, c - 7) / 9
      return `<circle cx="${c * 28 + 6}" cy="${r * 28 + 6}" r="${(13 * (1 - d)).toFixed(1)}"
        fill="${HUESO_2}" opacity=".9"/>`
    }).join('')).join('')}`),
  // Luz como objeto: panel perforado retroiluminado.
  luz: marco('#1A1410',
    `<defs><radialGradient id="lz" cx=".5" cy=".5" r=".62">
       <stop offset="0" stop-color="${AMBAR}"/><stop offset="1" stop-color="#1A1410"/></radialGradient></defs>
     <rect width="400" height="300" fill="url(#lz)"/>
     ${[...Array(10)].map((_, r) => [...Array(14)].map((_, c) =>
       `<circle cx="${c * 30 + 14 + (r % 2 ? 15 : 0)}" cy="${r * 32 + 12}" r="7" fill="#1A1410"/>`).join('')).join('')}`),
  // Envolventes técnicas: malla metálica de ventilación.
  env: marco('#181C1B',
    `${[...Array(9)].map((_, r) => [...Array(13)].map((_, c) => {
      const x = c * 32 + (r % 2 ? 16 : 0), y = r * 34
      return `<polygon points="${x},${y + 8} ${x + 14},${y} ${x + 28},${y + 8} ${x + 28},${y + 24}
        ${x + 14},${y + 32} ${x},${y + 24}" fill="none" stroke="${GALVA_CLR}" stroke-width="3.5"/>`
    }).join('')).join('')}`),
  // Nichos: hueco con profundidad real, luz rasante.
  nic: marco('#201914',
    `<rect x="52" y="-10" width="296" height="320" fill="${NOGAL}"/>
     <rect x="86" y="24" width="228" height="252" fill="#120E0B"/>
     <path d="M86 24h228l-34 34H120z" fill="${NOGAL_CLR}" opacity=".85"/>
     <path d="M86 276h228l-34 -34H120z" fill="${TINTA}" opacity=".6"/>
     <rect x="120" y="58" width="160" height="184" fill="${AMBAR}" opacity=".13"/>`)
}

/**
 * Patrón de celosía suelto, para usarlo como textura de fondo en superficies
 * grandes (portadas, vacíos). Se expone porque es el patrón del taller, no un
 * adorno: es la misma función que dibuja el producto.
 */
export const PATRON_CELOSIA = (paso = 64, color = '#FFFFFF') =>
  'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">` +
    celosiaSAT(paso, 5, color, color) + `</svg>`
  )

/** Declaración única de que este material es provisional. */
export const GRAFICOS_SON_PROVISIONALES = true
